/**
 * ForkAdmin (Super Admin / Control Tower) Responsive + RTL Design System Utilities
 *
 * Mirrors the ForkSeek (src/lib/responsive.ts) and ForkHire (src/lib/hireResponsive.ts)
 * responsive systems for the admin portal.
 *
 * Breakpoint strategy (shared across all portals):
 *   xs:  360        (standard mobile)
 *   sm:  640        (Tailwind default)
 *   md:  768
 *   lg:  1024
 *   xl:  1280
 *   2xl: 1536
 *   3xl: 1920       (large desktop / ultra-wide)
 *   4xl: 2560       (4K)
 */

/* ─── Page containers ─── */

/** Admin page container — wider default for dense data tables */
export const ADMIN_PAGE_CONTAINER =
  'max-w-[1400px] 3xl:max-w-[1600px] 4xl:max-w-[1800px] mx-auto w-full';

/** Narrow container for admin forms / detail dialogs */
export const ADMIN_NARROW_CONTAINER =
  'max-w-[640px] lg:max-w-[720px] mx-auto w-full';

/** Medium container for settings / config pages */
export const ADMIN_MEDIUM_CONTAINER =
  'max-w-[960px] 3xl:max-w-[1080px] mx-auto w-full';

/* ─── Spacing ─── */

/** Consistent vertical section spacing */
export const ADMIN_SECTION_SPACING = 'space-y-7 sm:space-y-7 lg:space-y-8';

/* ─── Typography ─── */

/** Page title classes — responsive font sizing + font-display */
export const ADMIN_PAGE_TITLE =
  'min-w-0 whitespace-normal break-words [overflow-wrap:anywhere] text-lg sm:text-xl lg:text-2xl font-bold text-foreground font-display';

/** Page subtitle / description */
export const ADMIN_PAGE_SUBTITLE =
  'min-w-0 whitespace-normal break-words [overflow-wrap:anywhere] text-sm text-muted-foreground';

/* ─── Page header ─── */

/** Standard page header — flex-wrap with direction-safe gap */
export const ADMIN_PAGE_HEADER =
  'flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5 xl:gap-4 min-w-0';

/* ─── Stat / KPI grids ─── */

/** 2→3→4 KPI grid for dashboard / overview pages */
export const ADMIN_KPI_GRID =
  'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 3xl:grid-cols-5 gap-4 sm:gap-5';

/** Stat card padding */
export const ADMIN_STAT_PAD = 'p-4 sm:p-4 lg:p-5';

/* ─── Card grids ─── */

/** Generic card grid */
export const ADMIN_CARD_GRID =
  'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6';

/* ─── Filter / toolbar ─── */

/** Filter toolbar — wraps gracefully on mobile, row on desktop */
export const ADMIN_FILTER_BAR =
  'flex flex-col sm:flex-row flex-wrap items-stretch sm:items-end gap-3';

/** Search input wrapper with icon (direction-aware) */
export const ADMIN_SEARCH_WRAPPER = 'relative w-full sm:w-auto sm:min-w-[240px]';
export const ADMIN_SEARCH_ICON =
  'absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none';
export const ADMIN_SEARCH_INPUT = 'ps-9';

/* ─── Table wrapper ─── */

/**
 * Responsive table wrapper — horizontal scroll on mobile,
 * full width on desktop. Adds rounded border container.
 */
export const ADMIN_TABLE_WRAPPER =
  'bg-card rounded-lg border border-border overflow-x-auto';

/* ─── Dashboard split layout ─── */

/** Two-column admin dashboard: main + sidebar panel */
export const ADMIN_DASHBOARD_GRID =
  'grid grid-cols-1 lg:grid-cols-5 gap-5 lg:gap-6 3xl:gap-8';

/* ─── RTL flip ─── */

/** Directional icon flip class */
export const RTL_FLIP = 'rtl-flip';
